import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, combineLatest, Observable } from 'rxjs';
import { map, startWith, tap, takeUntil } from 'rxjs/operators';
import { TuiAlertService } from '@taiga-ui/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import {
  ServiceDetail,
  ProductBase,
  ProviderServiceDetail,
  GetServiceDetailProviderResponse,
  AddProviderRequest,
  AddProviderResponse,
} from 'src/app/models/service-detail/service-detail.model';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-modal-assign-provider',
  templateUrl: './modal-assign-provider.component.html',
  styleUrls: ['./modal-assign-provider.component.css'],
})
export class ModalAssignProviderComponent implements OnInit, OnDestroy {
  @Input() orderHash = '';
  @Input() serviceInfo!: ServiceDetail;
  @Input() product!: ProductBase;
  @Input() idItv = '';
  @Output() providerAssigned = new EventEmitter<void>();

  SaveLoading: boolean = false;

  ZoneId: string | undefined;
  ProviderSelected: ProviderServiceDetail | undefined = undefined;

  /** Forms */
  readonly assignForm: FormGroup;
  readonly filterForm: FormGroup;

  /** State */
  private providers$ = new BehaviorSubject<ProviderServiceDetail[]>([]);
  readonly filteredProviders$: Observable<ProviderServiceDetail[]>;
  readonly listLoading$ = new BehaviorSubject<boolean>(false);
  readonly buttonLoading$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  constructor(
    fb: FormBuilder,
    private offcanvas: NgbOffcanvas,
    private hs: HistoryServicesService,
    private alerts: TuiAlertService,
    private translate: TranslateService
  ) {
    this.assignForm = fb.group({
      orderHash: ['', Validators.required],
      providerId: ['', Validators.required],
    });
    this.filterForm = fb.group({
      nameFilter: [''],
    });

    this.filteredProviders$ = combineLatest([
      this.providers$,
      this.filterForm.get('nameFilter')!.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([list, term]) => {
        return list.filter((p) =>
          p.name.toLowerCase().includes(term.toLowerCase())
        );
      })
    );
  }

  ngOnInit(): void {
    // this.assignForm.patchValue({ orderHash: this.orderHash });
    // Load providers once inputs are initialized
    // this.loadProviders();

    this.ZoneId = this.serviceInfo.zoneId;
  }

  // private loadProviders(): void {
  //   this.listLoading$.next(true);

  //   this.hs
  //     .getProviders(this.serviceInfo.zoneId)
  //     .pipe(
  //       map((res: GetServiceDetailProviderResponse) => {
  //         return res.providersInZone;
  //       }),
  //       map((providers) => this.filterByLocalization(providers)),
  //       tap(() => {
  //         this.listLoading$.next(false);
  //       }),
  //       takeUntil(this.destroy$)
  //     )
  //     .subscribe({
  //       next: (filtered) => {
  //         this.providers$.next(filtered);
  //       },
  //       error: (err) => {
  //         this.listLoading$.next(false);
  //         this.alerts
  //           .open(
  //             this.translate.instant(
  //               'modals.assign_provider.load_providers_error'
  //             ),
  //             { status: 'error', autoClose: true }
  //           )
  //           .subscribe();
  //       },
  //     });
  // }

  /**
   * [ER - 06/05/2025] - Esta manera de filtrar por itv no es la mas correcta habria que agregar valores en BD
   * Decide qué clave usar para filtrar los providers:
   * - Por defecto, product.productId
   * - Para ciertos productLocalizationCode, usar idItv;
   *   si idItv no está definido, retornar cadena vacía para evitar filtrar.
   */
  private getFilteringKey(): string {
    const code = this.product.productLocalizationCode ?? '';
    const specialCodes: string[] = ['360-mot-fuel-car-es', 'mot-homologation'];

    if (specialCodes.includes(code)) {
      if (this.idItv) {
        return this.idItv;
      }
      return '';
    }
    return this.product.productId;
  }

  private filterByLocalization(
    list: ProviderServiceDetail[]
  ): ProviderServiceDetail[] {
    const key = this.getFilteringKey();
    if (!key) {
      return list;
    }
    const filtered = list.filter((p) => p.productIds.includes(key));
    return filtered;
  }

  selectProvider(p: ProviderServiceDetail): void {
    this.assignForm.patchValue({ providerId: p.id });
  }

  assignProvider(): void {
    if (!this.ProviderSelected) {
      this.showInlineError(
        this.translate.instant('modals.assign_provider.select_provider_error')
      );
      return;
    }

    this.buttonLoading$.next(true);
    const payload: AddProviderRequest = {
      orderHash: this.orderHash,
      productId: this.product.productId,
      providerId: this.ProviderSelected?.id,
    };

    this.hs
      .addProviderToProductInService(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AddProviderResponse) => {
          this.buttonLoading$.next(false);
          if (!res.wasSuccessful) {
            this.showInlineError(
              this.translate.instant('modals.assign_provider.assign_failed')
            );
            return;
          }
          this.providerAssigned.emit();
          this.offcanvas.dismiss();
          this.hs.publish('updateDetailsService');
          this.alerts
            .open(this.translate.instant('modals.assign_provider.success'), {
              status: 'success',
              autoClose: true,
            })
            .subscribe();
        },
        error: (err) => {
          this.buttonLoading$.next(false);
          this.showInlineError(
            this.translate.instant('modals.assign_provider.unknown_error')
          );
        },
      });
  }

  private showInlineError(msg: string): void {
    const container = document.getElementById(
      'modal_assign_provider_service_alert'
    );
    if (!container) return;
    const alertDiv = document.createElement('div');
    alertDiv.className = 'tui-alert tui-alert_error';
    alertDiv.textContent = msg;
    container.appendChild(alertDiv);
    setTimeout(() => container.removeChild(alertDiv), 4000);
  }

  closeCanvas(): void {
    this.offcanvas.dismiss();
  }

  // trackById(_index: number, p: ProviderServiceDetail): string {
  //   return p.id;
  // }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
